import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Eye, Users, Globe, Award, CheckCircle } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Reliability',
      description: 'We deliver on our promises with consistent quality and timely execution.',
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Our extensive network spans continents, connecting you to the best suppliers.',
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We work alongside our clients as strategic partners, not just service providers.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our procurement services.',
    },
  ];

  const milestones = [
    { year: '2022', event: 'Founded in Brussels, Belgium' },
    { year: '2023', event: 'Expanded operations to West Africa' },
    { year: '2024', event: 'Established presence in multiple African countries' },
    { year: '2025', event: 'Growing supplier network and client base' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative bg-[#0F172A] pt-32 pb-20 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              About Us
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Building Bridges in Global Procurement
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Supply Hub is a leading international procurement and sourcing company, 
              specializing in connecting businesses with reliable suppliers across the globe.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Mission */}
            <div data-testid="mission-section" className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-[#0EA5E9]"></div>
              <div className="pl-8">
                <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mb-6">
                  <Target className="text-white" size={32} />
                </div>
                <h2 className="font-['Outfit'] text-3xl font-semibold text-[#0F172A] mb-4">
                  Our Mission
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed">
                  To simplify international procurement and empower businesses with efficient, 
                  cost-effective sourcing solutions. We bridge the gap between global suppliers 
                  and African markets, ensuring quality, reliability, and value in every transaction.
                </p>
              </div>
            </div>

            {/* Vision */}
            <div data-testid="vision-section" className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-[#F97316]"></div>
              <div className="pl-8">
                <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mb-6">
                  <Eye className="text-white" size={32} />
                </div>
                <h2 className="font-['Outfit'] text-3xl font-semibold text-[#0F172A] mb-4">
                  Our Vision
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed">
                  To become the most trusted procurement partner in Africa, recognized for 
                  our expertise, integrity, and commitment to client success. We envision 
                  a future where businesses can source globally with confidence and ease.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section data-testid="story-section" className="bg-[#F8FAFC] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
                Our Story
              </span>
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-[#0F172A] mt-4 mb-6">
                A Journey of Excellence
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Founded in Brussels, Belgium, Supply Hub emerged from a vision to transform 
                  how businesses approach international procurement. Our founders recognized 
                  the challenges companies face when sourcing products across borders — 
                  language barriers, quality concerns, logistical complexities, and 
                  supplier reliability.
                </p>
                <p>
                  With deep expertise in DRC, Central African markets and a robust global network, we 
                  positioned ourselves as the bridge between international suppliers and 
                  businesses operating in Africa. Today, we proudly serve clients across 
                  the Democratic Republic of Congo, Central Africa and beyond, managing complex 
                  procurement processes with precision and care.
                </p>
                <p>
                  Our success is built on relationships — with our clients, our suppliers, 
                  and our team. We believe in transparency, efficiency, and delivering 
                  value at every step of the supply chain.
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white border border-slate-200 p-8">
              <h3 className="font-['Outfit'] text-xl font-semibold text-[#0F172A] mb-8">
                Key Milestones
              </h3>
              <div className="space-y-6">
                {milestones.map((milestone, index) => (
                  <div key={milestone.year} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 bg-[#0EA5E9] rounded-full"></div>
                      {index < milestones.length - 1 && (
                        <div className="w-px h-full bg-slate-200 my-1"></div>
                      )}
                    </div>
                    <div className="pb-4">
                      <span className="font-['JetBrains_Mono'] text-[#0EA5E9] text-sm font-medium">
                        {milestone.year}
                      </span>
                      <p className="text-slate-600 text-sm mt-1">{milestone.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              What Drives Us
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-[#0F172A] mt-4">
              Our Core Values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="group border border-slate-200 p-8 hover:border-[#0EA5E9] hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#F8FAFC] flex items-center justify-center mb-6 group-hover:bg-[#0EA5E9] transition-colors">
                  <value.icon className="text-[#0F172A] group-hover:text-white transition-colors" size={24} />
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section data-testid="differentiators-section" className="bg-[#0F172A] py-20 md:py-32 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
                What Sets Us Apart
              </span>
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-white mt-4 mb-8">
                Your Strategic Procurement Partner
              </h2>
              <ul className="space-y-4">
                {[
                  'Extensive network of vetted international suppliers',
                  'Deep understanding of African market dynamics',
                  'End-to-end procurement management',
                  'Transparent pricing and processes',
                  'Dedicated account management',
                  'Quality assurance at every step',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#0EA5E9] mt-0.5 flex-shrink-0" size={20} />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 border border-[#0EA5E9]/20"></div>
              <img
                src="/2.jpeg"
                alt="Supply Hub Diverse Team"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0EA5E9] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-white mb-4">
            Ready to Partner with Supply Hub?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Let's discuss how we can optimize your procurement process.
          </p>
          <Link to="/contact">
            <Button
              data-testid="about-cta-btn"
              className="bg-white text-[#0EA5E9] hover:bg-[#F8FAFC] rounded-none px-8 py-4 font-medium transition-colors duration-300"
            >
              Get in Touch
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
